import React, {useState, useEffect, useCallback} from "react";
import "./ConfigPage.scss";
import ConfigWheel from "./ConfigWheel";
import configImages from "./configImages";
import {greeting} from "../../portfolio";
import {useLocalStorage} from "../../hooks/useLocalStorage";

/* Standalone page served at /config. It is intentionally unlinked from the
   portfolio nav — reachable only by typing the URL. */

export default function ConfigPage() {
  const darkPref = window.matchMedia("(prefers-color-scheme: dark)");
  const [isDark, setIsDark] = useLocalStorage("isDark", darkPref.matches);
  const [lightbox, setLightbox] = useState(null);

  const rows = [
    {title: "GUIs", items: configImages.guis},
    {title: "Holograms", items: configImages.holograms},
    {title: "Lore", items: configImages.lore}
  ];

  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = event => {
      if (event.key === "Escape") closeLightbox();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, closeLightbox]);

  return (
    <div className={isDark ? "dark-mode cfg-page" : "cfg-page"}>
      <header className="cfg-header">
        <a href="/" className="cfg-logo">
          <span className="cfg-logo-bracket">&lt;</span>
          <span className="cfg-logo-name">{greeting.username}</span>
          <span className="cfg-logo-bracket">/&gt;</span>
        </a>
        <div className="cfg-header-right">
          <button
            type="button"
            className="cfg-theme-toggle"
            onClick={() => setIsDark(!isDark)}
            aria-label="Toggle theme"
          >
            <i className={isDark ? "fas fa-sun" : "fas fa-moon"}></i>
          </button>
          <a href="/" className="cfg-back">
            <i className="fas fa-arrow-left"></i>&nbsp;&nbsp;Back to portfolio
          </a>
        </div>
      </header>

      <main className="cfg-rows">
        {rows.map(row => (
          <ConfigWheel
            key={row.title}
            title={row.title}
            count={
              row.items.length === 1
                ? "1 config"
                : `${row.items.length} configs`
            }
            items={row.items}
            onOpen={setLightbox}
          />
        ))}
      </main>

      <footer className="cfg-footer">
        <p>Built and configured by {greeting.username}</p>
      </footer>

      {lightbox && (
        <div
          className="cfg-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.label}
        >
          <button
            type="button"
            className="cfg-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <i className="fas fa-times"></i>
          </button>
          <figure
            className="cfg-lightbox-inner"
            onClick={event => event.stopPropagation()}
          >
            <img src={lightbox.src} alt={lightbox.label} />
            <figcaption>{lightbox.label}</figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
